'use client';

import { useState, useRef, useCallback } from 'react';
import useIsomorphicLayoutEffect from './helpers/use-isomorphic-layout-effect.js';
import getScrollableParents from './helpers/getScrollableParents.js';
import deepEqual from './helpers/deepEqual.js';
import computePosition from './core/computePosition.js';
import autoUpdate from './core/autoUpdate.js';
import FloatingPortal from './FloatingPortal.js';

var useFloating = function (options) {
    var _a = options.position, position = _a === void 0 ? 'absolute' : _a, placement = options.placement, config = options.config;
    var _b = (config || {}).autoUpdate, autoUpdate$1 = _b === void 0 ? true : _b;
    var _c = useState(null), _reference = _c[0], _setReference = _c[1];
    var _d = useState(null), _popup = _d[0], _setPopup = _d[1];
    var referenceRef = useRef(null);
    var popupRef = useRef(null);
    var scrollableParents = useRef(new Set());
    var _e = useState({
        x: 0,
        y: 0,
        position: position,
        placement: placement,
        isReferenceHidden: false
    }), context = _e[0], setContext = _e[1];
    var contextRef = useRef();
    /*
     * Custom function to avoid node being set multiple time
     * */
    var setReference = useCallback(function (node) {
        if (node !== referenceRef.current) {
            referenceRef.current = node;
            _setReference(node);
        }
    }, [_setReference]);
    var setPopup = useCallback(function (node) {
        if (node !== popupRef.current) {
            popupRef.current = node;
            _setPopup(node);
        }
    }, [_setPopup]);
    /*
     * Update function will be called to calculated popup position
     * */
    var update = useCallback(function () {
        if (!referenceRef.current || !popupRef.current) {
            return;
        }
        var computePositionConfigs = {
            placement: placement,
            position: position,
            scrollableParents: scrollableParents.current,
            popupConfigs: config
        };
        var context = computePosition(referenceRef.current, popupRef.current, computePositionConfigs);
        if (!deepEqual(context, contextRef.current)) {
            contextRef.current = context;
            setContext(context);
        }
    }, [placement, position, config]);
    useIsomorphicLayoutEffect(function () {
        if (_reference) {
            referenceRef.current = _reference;
        }
        if (_popup) {
            popupRef.current = _popup;
        }
        if (_reference && _popup) {
            var target = position === 'fixed' ? _reference : _popup;
            var scrollableParentsByTarget = getScrollableParents(target);
            if (scrollableParents.current.size === 0 && scrollableParentsByTarget.size > 0) {
                /*
                 * Get and store scrollableParents
                 * */
                scrollableParents.current = scrollableParentsByTarget;
            }
            /*
             * Update function will calculate the position of popup element
             * */
            if (autoUpdate$1) {
                var clear_1 = autoUpdate(scrollableParents.current, update);
                /*
                 * Observer the changes of popup element when auto update is enable
                 * */
                var observer_1 = new MutationObserver(update);
                observer_1.observe(_popup, {
                    attributes: true,
                    childList: true,
                    subtree: true
                });
                return function () {
                    clear_1();
                    observer_1.disconnect();
                };
            }
            update();
        }
        return function () { };
    }, [_reference, _popup, update, position, autoUpdate$1]);
    var refs = {
        setReference: setReference,
        setPopup: setPopup,
        reference: referenceRef,
        popup: popupRef
    };
    return {
        refs: refs,
        context: context,
        FloatingPortal: FloatingPortal
    };
};

export { useFloating as default };
//# sourceMappingURL=index.js.map
